import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Avatar,
  Box,
  Tab,
  Tabs,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  MenuItem,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AvatarImg, menAvatar, scannerImg } from "../assets/images";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  CartIcon,
  EditIcon,
  EditWithUnderscroreIcon,
  GoogleIcon,
  LinkdinIcon,
  MicrosoftIcon,
  ModalClose,
} from "../assets/icons";
import SecurityLinkdinIcon from "../assets/icons/securityLinkdinIcon";
import AuthService from "../services/auth.service";
import { useForm, Controller } from "react-hook-form";
import { UpdateProfileRequest } from "../types/auth";
import { decodeIdToken, decodeMetadataToken } from "../utils/token.utils";
import moment from 'moment-timezone';
import { Applications } from "../types/enums";
import UpdateDomainModal from "../modals/UpdateDomain";
import { toast } from "react-toastify";
import theme from "../theme";
import UpdateUserDomainModal from "../modals/UpdateUserDomain";
import MuiPhone from "./MuiPhone";
import DeletePopup from "../modals/DeletePopup";
import BuyAddOns from "../modals/BuyAddOns";
import Loader from "./common/Loader";

interface SettingsProps {
  initialTab?: number;
}

interface UserDetails {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  company?: string;
  job_title?: string;
  phone_number?: string;
  preferred_username?: string;
  picture?: string;
  zoneinfo?: string;
  domains?: {
    EASYWEBINAR?: string;
    EASYCRM?: string;
  };
  'custom:job_title'?: string;
}

interface CompanyData {
  userId: string;
  companyId: string;
  companyName: string;
  domains: {
    easycrm: string;
    easywebinar: string;
  }
  customDomain: string;
  customDomainStatus: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  parentCompanyId: string;
  jobTitle?: string;
  phoneNumber: string;
  picture?: string;
  username?: string;
  timezone?: string;
  subscription?: {
    id: string;
    status?: string;
    items?: {
      itemPriceId: string;
      itemType: string;
      quantity: number;
      amount: number;
    }[];
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const Settings: React.FC<SettingsProps> = ({ initialTab }) => {
  const [value, setValue] = useState(initialTab || 0);
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [userDetails, setUserDetails] = useState<UserDetails>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [timezones, setTimezones] = useState<any[]>([]);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(false);

  const [deleteAddOnDialog, setDeleteAddOnDialog] = useState(false);
  const [buyAddOnsDialog, setBuyAddOnsDialog] = useState(false);

  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyData | null>(null);

  // Add hidden file input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add phone state and handlers
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneBlurred, setPhoneBlurred] = useState(false);

  const metadata = decodeMetadataToken();
  const client_email = metadata.client_email;
  
  const parseStoredTimezone = useCallback((timezoneString: string) => {
    if (!timezoneString) return '';
    const [offset, zoneName] = timezoneString.split(',');
    return zoneName || '';
  }, []);

  const getTimezoneString = useCallback(() => {
    // Default to 'UTC' if no timezones available
    if (timezones.length === 0) return 'UTC';
    
    // Set UTC as the default timezone
    return 'UTC';
  }, [timezones]);

  const formatTimezoneForStorage = useCallback((zoneName: string) => {
    // Find the timezone object
    const timezone = timezones.find(tz => tz.timezone_name === zoneName);
    if (!timezone) return zoneName;
    
    // Format according to the required pattern: (GMT+05:30),Asia/Calcutta
    const symbol = timezone.timezone_gmt_symbol;
    const offset = timezone.GMT;

    return `(GMT${symbol}${offset}),${zoneName}`;
  }, [timezones]);
  
  const fetchTimezones = useCallback(async () => {
    try {
      setIsLoadingTimezones(true);
      const response = await AuthService.getEasyWebinarTimezones();
      setTimezones(response.data);
    } catch (error) {
      console.error('Error fetching timezones:', error);
    } finally {
      setIsLoadingTimezones(false);
    }
  }, []);

  useEffect(() => {
    fetchTimezones();
  }, [fetchTimezones]);

  const handlePhoneChange = (value: string) => {
    // Only update the state, don't do any processing
    setPhoneValue(value);
  };

  const handlePhoneBlur = () => {
    console.log('Phone value:', phoneValue);
    setPhoneBlurred(true);
  };

  const handleCancelClose = () => {
    setDeleteAddOnDialog(false);
  };

  const handleDialogBuyAddOns = () => {
    setBuyAddOnsDialog(true);
  };

  const handleDialogBuyAddOnsClose = () => {
    setBuyAddOnsDialog(false);
  };

  const handleDialogBuyAddOnsConfirm = () => {
    setBuyAddOnsDialog(false);
    fetchCompanies();
  };

  const handleCancelAddOnConfirm = async () => {
    setDeleteAddOnDialog(false);
    try {
      const subscriptionId = selectedCompany?.subscription?.id;

      if (!subscriptionId) {
        console.error("No subscription ID available");
        return;
      }

      // Get Chargebee instance
      const chargebeeInstance = (window as any).Chargebee?.getInstance();
      if (!chargebeeInstance) {
        throw new Error("Chargebee not initialized");
      }

      const cbPortal = chargebeeInstance.createChargebeePortal();
      const callbacks = {
        close: async () => {
          console.log('Edit subscription section closed');
          // refetch companies data
          try {
            await fetchCompanies();
          } catch (refreshError) {
            console.error('Failed to refresh after subscription edit:', refreshError);
          }
        },
        loaded: () => {
          console.log('Edit subscription section loaded');
        },
        error: (error: any) => {
          console.error('Portal error:', error);
        }
      };

      cbPortal.open(callbacks, {
        sectionType: (window as any).Chargebee.getPortalSections().EDIT_SUBSCRIPTION,
        params: {
          subscriptionId: subscriptionId
        }
      });

    } catch (error) {
      console.error("Error cancelling addon:", error);
      toast.error("An error occurred while cancelling the add-on");
    } finally {
      setBuyAddOnsDialog(false);
    }
  };

  const hasCustomDomainAddOn = (company: CompanyData): boolean => {
    return !!company.subscription?.items?.some(
      item => item.itemPriceId === 'custom-domain' && item.itemType === 'addon'
    );
  };

  const handleCompanyAction = (company: CompanyData, action: 'edit' | 'cancel' | 'buy') => {
    setSelectedCompany(company);
    if (action === 'edit') {
      handleCustomDomainUpdateOpen();
    } else if (action === 'cancel') {
      setDeleteAddOnDialog(true);
    } else if (action === 'buy') {
      handleDialogBuyAddOns();
    }
  };

  const { control, handleSubmit, reset } = useForm<UserDetails>({
    defaultValues: {
      ...userDetails,
      zoneinfo: 'UTC'
    },
  });

  const fetchCompanies = useCallback(async () => {
    try {
      setIsLoading(true);
      const metadata = decodeMetadataToken();
      const companyId = metadata?.companies?.[0]?.id;
      const parentCompanyId = companyId;
      const response = await AuthService.getCompaniesByParentId(parentCompanyId);
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (value === 2) {
      fetchCompanies();
    }
  }, [fetchCompanies, value]);

  useEffect(() => {
    if (timezones.length === 0) return; // Wait for timezones to be loaded
    
    try {
      const decoded = decodeIdToken() as UserDetails;
      const metadata = decodeMetadataToken();

      const parsedTimezone = decoded.zoneinfo ? parseStoredTimezone(decoded.zoneinfo) : getTimezoneString();
      const updatedDecoded = {
        ...decoded,
        zoneinfo: parsedTimezone,
        company: metadata?.companies?.[0]?.name || '',
        domains: metadata?.companies?.[0]?.domains || {},
        job_title: decoded['custom:job_title'] || ''
      };

      setUserDetails(updatedDecoded);
      setPhoneValue(decoded.phone_number || '');
      reset(updatedDecoded);
    } catch (error) {
      console.error("Error decoding tokens:", error);
    }
  }, [reset, timezones, parseStoredTimezone, getTimezoneString]);

  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3, px: { xs: 0 } }}>{children}</Box>}
      </div>
    );
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);
  
  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
  
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleEnableTFA = () => {
    setIsEnabled(true);
  };

  const handleDisableTFA = () => {
    setIsEnabled(false);
  };

  const handleOldPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOldPassword(e.target.value);
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    if (!confirmPassword) {
      setPasswordError("");
    } else if (e.target.value !== confirmPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value);
    if (e.target.value !== newPassword) {
      setPasswordError("Passwords do not match");
    } else {
      setPasswordError("");
    }
  };

  const handleUpdatePassword = async () => {
    try {
      const response = await AuthService.changePassword({
        oldPassword,
        newPassword,
      });

      if (response.status === 200) {
        toast.success("Password updated successfully");
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordError("");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to update password";
      toast.error(errorMessage);
      console.error("Error updating password:", error);
    }
  };

  const calculatePasswordStrength = (password: string): number => {
    if (!password) return 0;

    let strength = 0;

    // Length check
    if (password.length >= 8) strength++;

    // Contains lowercase
    if (/[a-z]/.test(password)) strength++;

    // Contains uppercase
    if (/[A-Z]/.test(password)) strength++;

    // Contains number
    if (/[0-9]/.test(password)) strength++;

    // Contains special character
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    return Math.min(strength, 4);
  };

  const getStrengthColor = (strength: number): string => {
    switch (strength) {
      case 0:
        return "#D9D9D9"; // Gray - Empty
      case 1:
        return "#E44258"; // Red - Weak
      case 2:
        return "#DEA83F"; // Orange - Fair
      case 3:
        return "#26BF94"; // Green - Good
      case 4:
        return "#26BF94"; // Green - Strong
      default:
        return "#D9D9D9"; // Gray - Default
    }
  };

  const getStrengthText = (strength: number): string => {
    switch (strength) {
      case 0:
        return "";
      case 1:
        return "Weak";
      case 2:
        return "Fair";
      case 3:
        return "Good";
      case 4:
        return "Strong";
      default:
        return "";
    }
  };

  const handleProfileUpdate = async (data: UserDetails) => {
    try {
      // Create an object to store only the changed fields
      const changedFields: UpdateProfileRequest = {};

      // Form submission
      // Check if either first name or last name has changed
      if (data.given_name !== userDetails.given_name || data.family_name !== userDetails.family_name) {
        changedFields.given_name = data.given_name;
        changedFields.family_name = data.family_name;
      }

      // Compare remaining fields with the original userDetails
      if (data.zoneinfo !== userDetails.zoneinfo) {
        changedFields.zoneinfo = data.zoneinfo ? formatTimezoneForStorage(data.zoneinfo) : undefined;
      }
      if (data.company !== userDetails.company) {
        changedFields.companyName = data.company;
      }
      if (data.preferred_username !== userDetails.preferred_username) {
        changedFields.username = data.preferred_username;
      }
      if (data.job_title !== userDetails.job_title) {
        changedFields.jobTitle = data.job_title;
      }
      
      changedFields.phoneNumber = phoneValue;

      // Only make the API call if there are changes
      if (Object.keys(changedFields).length > 0) {
        setIsUpdating(true);
        const response = await AuthService.updateProfile(changedFields);
        if (response.status === 200) {
          // Update userDetails with the new data
          setUserDetails(prev => ({
            ...prev,
            ...data,
            phone_number: phoneValue || prev.phone_number
          }));
          toast.success("User details updated successfully");
        }
      } else {
        toast.info("No changes to update");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to update profile";
      toast.error(errorMessage);
      console.error("Error updating profile:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  //Custom domain tab
  const [editDomainDialogOpen, setEditDomainDialogOpen] = useState(false);
  const [editCustomDomainDialogOpen, setEditCustomDomainDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setEditDomainDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDomainDialogOpen(false);
  };

  const handleCustomDomainUpdateOpen = () => {
    setEditCustomDomainDialogOpen(true);
  };

  const handleCustomDomainUpdateClose = () => {
    setEditCustomDomainDialogOpen(false);
  };

  const handleCustomDomainUpdate = async () => {
    await fetchCompanies();
    handleCustomDomainUpdateClose();
  }

  const handleConfirm = async () => {
    await refreshToken();
    try {
      const decoded = decodeIdToken() as UserDetails;
      const metadata = decodeMetadataToken();

      const parsedTimezone = decoded.zoneinfo ? parseStoredTimezone(decoded.zoneinfo) : getTimezoneString();
      const updatedDecoded = {
        ...decoded,
        zoneinfo: parsedTimezone,
        company: metadata?.companies?.[0]?.name || '',
        domains: metadata?.companies?.[0]?.domains || {}
      };

      setUserDetails(updatedDecoded);
      reset(updatedDecoded);
    } catch (error) {
      console.error("Error decoding tokens:", error);
    }
    setEditDomainDialogOpen(false);
  };

  const refreshToken = () => {
    return AuthService.refreshToken();
  }

  // Add file upload handler
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingPhoto(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await AuthService.uploadProfilePhoto(formData);

      if (response.status === 200) {
        // Call updateProfile API with just the picture URL
        const updateResponse = await AuthService.updateProfile({
          picture: response.data.url
        });

        if (updateResponse.status === 200) {
          setUserDetails(prev => ({
            ...prev,
            picture: response.data.url
          }));
          toast.success("Profile photo updated successfully");
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to upload photo";
      toast.error(errorMessage);
    } finally {
      setIsUploadingPhoto(false);
      // Reset the file input value
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Add click handler for edit overlay
  const handleEditPhotoClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <Box maxWidth="1200px" mx="auto">
        <Box mb={{ xs: "34px", sm: "20px", xl: "28px" }}>
          <Typography
            color="#333333"
            fontSize="24px"
            mb="15px"
            fontWeight="500"
          >
            Account Settings
          </Typography>
          <Typography>Update your account information</Typography>
        </Box>

        <Box sx={{ width: "100%" }} className="settings-module-listing">
          <Box sx={{ borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="basic tabs example"
            >
              <Tab
                label="Profile Information"
                {...a11yProps(0)}
                sx={{ textTransform: "capitalize" }}
              />
              <Tab
                label="Change Password"
                {...a11yProps(1)}
                sx={{ textTransform: "capitalize" }}
              />

              <Tab label="User Domain" {...a11yProps(2)} sx={{ textTransform: 'capitalize' }} />
              {/* <Tab
                label="Sign In & Security"
                {...a11yProps(3)}
                sx={{ textTransform: "capitalize" }}
              /> */}
            </Tabs>
          </Box>
          {/* first panel content */}
          {value === 0 && (
            <Grid
              container
              rowSpacing={{ xs: 3, sm: 3, md: 3 }}
              columnSpacing={{ xs: 3, sm: 2, md: 3, xl: 6 }}
            >
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 8 }}>
                <Grid
                  container
                  rowSpacing={{ xs: 3, sm: 3, md: 3 }}
                  columnSpacing={{ xs: 3, sm: 2, md: 3 }}
                  borderRadius="20px"
                  sx={{ background: "#fff" }}
                  padding={{ xs: "16px", sm: "24px", md: "32px" }}
                  boxShadow="0px 4px 12px 0px rgba(43, 43, 48, 0.06)"
                >
                  <Grid size={12}>
                    <Typography fontSize="18px" fontWeight="500" marginBottom="24px">Personal Information</Typography>
                    <Box display="flex" alignItems="center">
                      <Box
                        position="relative"
                        borderRadius="100%"
                        sx={{
                          "&:hover": {
                            "& .upload-overlay": {
                              display: "flex",
                            },
                          },
                        }}
                      >
                        <Avatar
                          alt=""
                          src={userDetails?.picture}
                          sx={{ width: 72, height: 72, border: "1px solid #178EFC" }}
                        />
                        <Box
                          height={"100%"}
                          width={"100%"}
                          position={"absolute"}
                          top={"0"}
                          display="none"
                          alignItems="center"
                          justifyContent="center"
                          borderRadius={"39px"}
                          sx={{
                            border: "1px solid #FFFFFF",
                            backgroundColor: "rgba(0, 0, 0, 0.60)",
                            cursor: isUploadingPhoto ? "wait" : "pointer",
                          }}
                          className="upload-overlay"
                          onClick={handleEditPhotoClick}
                        >
                          {isUploadingPhoto ? (
                            <CircularProgress size={24} sx={{ color: '#fff' }} />
                          ) : (
                            <EditWithUnderscroreIcon />
                          )}
                        </Box>
                      </Box>
                      <Box ml="16px" sx={{ wordBreak: "break-word" }}>
                        <Typography mb="4px" color="#000000" fontSize="18px">
                          {userDetails?.name || ""}
                        </Typography>
                        <Typography color="#333" fontSize="16px">
                          {userDetails?.email}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      First Name
                    </Typography>
                    <Controller
                      name="given_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          placeholder="Enter first Name"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      Last Name
                    </Typography>
                    <Controller
                      name="family_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          placeholder="Enter last Name"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      Company
                    </Typography>
                    <Controller
                      name="company"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          placeholder="Enter Company Name"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      Job Title
                    </Typography>
                    <Controller
                      name="job_title"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          placeholder="Enter Job Title"
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      Username
                    </Typography>
                    <Controller
                      name="preferred_username"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          placeholder="Enter User ID"
                          disabled
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      Phone Number
                    </Typography>
                    <MuiPhone
                      value={phoneValue}
                      onChange={handlePhoneChange}
                      onBlur={handlePhoneBlur}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          borderColor: "#C7C7C7",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#333333",
                          border: "1px solid",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "14px 16px",
                          background: "#ffffff",
                          borderRadius: "8px",
                        },
                        "&.MuiInputBase-input::placeholder": {
                          color: "#333333",
                          fontSize: "16px",
                          opacity: "0.8",
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      Email
                    </Typography>
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          placeholder="Email"
                          disabled
                          fullWidth
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography
                      color="#333333"
                      fontSize="14px"
                      fontWeight="500"
                      mb="12px"
                      sx={{ opacity: "0.6" }}
                    >
                      Timezone
                    </Typography>
                    <Controller
                      name="zoneinfo"
                      control={control}
                      defaultValue="UTC"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          variant="outlined"
                          fullWidth
                          slotProps={{
                            select: {
                              MenuProps: {
                                PaperProps: {
                                  style: {
                                    maxHeight: 300,
                                  },
                                },
                              },
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "& .MuiSelect-select": {
                              display: 'flex',
                              alignItems: 'center'
                            }
                          }}
                        >
                          {isLoadingTimezones ? (
                            <MenuItem disabled>
                              <Box display="flex" alignItems="center" gap={1}>
                                <CircularProgress size={20} /> Loading timezones...
                              </Box>
                            </MenuItem>
                          ) : (
                            timezones.map((timezone) => (
                              <MenuItem
                                key={timezone.webinar_timezone_id_pk}
                                value={timezone.timezone_name}
                                sx={{
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {timezone.name}
                              </MenuItem>
                            ))
                          )}
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>Domain</Typography>
                    <Box display="flex" alignItems="center">
                      <TextField
                        variant="outlined"
                        value={`https://${userDetails?.domains?.EASYWEBINAR || ''}.easywebinar.live`}
                        placeholder='https://your-domain.easywebinar.live'
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: '8px',
                            borderColor: '#C7C7C7',
                          },

                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: '#333333',
                            border: '1px solid'
                          },

                          "& .MuiOutlinedInput-input": {
                            padding: '14px 16px',
                            background: '#ffffff',
                            borderRadius: '8px'
                          },
                          "&.MuiInputBase-input::placeholder": {
                            color: '#333333',
                            fontSize: '14px',
                            opacity: '0.8'
                          },
                        }}
                        fullWidth
                      />
                      <Box ml="12px" sx={{ cursor: 'pointer' }} onClick={handleDialogOpen}><EditIcon color={theme.palette.primary.main} /></Box>
                      <UpdateDomainModal
                        open={editDomainDialogOpen}
                        onClose={handleDialogClose}
                        domain=".easywebinar.live"
                        application={Applications.EASYWEBINAR}
                        onConfirm={handleConfirm}
                        initialValue={userDetails?.domains?.EASYWEBINAR || ''}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Button
                  type="submit"
                  variant="contained"
                  onClick={handleSubmit(handleProfileUpdate)}
                  disabled={isUpdating || client_email}
                  sx={{
                    height: "52px",
                    fontSize: "16px",
                    borderRadius: "8px",
                    marginTop: "32px",
                    paddingX: "64px",
                  }}
                >
                  {isUpdating ? <><CircularProgress size={20} color="inherit" /> &nbsp; Updating...</> : "Update"}
                </Button>
              </Grid>
              {/* <Grid
                size={{ xs: 12, sm: 12, md: 12, lg: 4 }}
                mt={{ xs: 3, lg: "0" }}
              >
                <Box
                  borderRadius={"20px"}
                  border={"1px solid #E8EAF0"}
                  boxShadow={"0px 4px 12px 0px rgba(43, 43, 48, 0.06)"}
                  p={4}
                  bgcolor="#ffffff"
                >
                  <Typography variant="h6" color="#000" fontSize={"18px"} mb={3}>
                    Social Media
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                      <Typography
                        color="#333333"
                        fontSize="14px"
                        fontWeight="500"
                        mb="12px"
                        sx={{ opacity: "0.6" }}
                      >
                        Facebook
                      </Typography>
                      <TextField
                        variant="outlined"
                        placeholder="www.facebook.com/ryan-lee21"
                        value={"www.facebook.com/ryan-lee21"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            borderColor: "#C7C7C7",
                          },

                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#333333",
                            border: "1px solid",
                          },

                          "& .MuiOutlinedInput-input": {
                            padding: "14px 16px",
                            background: "#ffffff",
                            borderRadius: "8px",
                          },
                          "&.MuiInputBase-input::placeholder": {
                            color: "#333333",
                            fontSize: "16px",
                            opacity: "0.8",
                          },
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                      <Typography
                        color="#333333"
                        fontSize="14px"
                        fontWeight="500"
                        mb="12px"
                        sx={{ opacity: "0.6" }}
                      >
                        Youtube
                      </Typography>
                      <TextField
                        variant="outlined"
                        placeholder="https://www.youtube.com/c/easywebinar"
                        value="https://www.youtube.com/c/easywebinar"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            borderColor: "#C7C7C7",
                          },

                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#333333",
                            border: "1px solid",
                          },

                          "& .MuiOutlinedInput-input": {
                            padding: "14px 16px",
                            background: "#ffffff",
                            borderRadius: "8px",
                          },
                          "&.MuiInputBase-input::placeholder": {
                            color: "#333333",
                            fontSize: "16px",
                            opacity: "0.8",
                          },
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                      <Typography
                        color="#333333"
                        fontSize="14px"
                        fontWeight="500"
                        mb="12px"
                        sx={{ opacity: "0.6" }}
                      >
                        Linkedin
                      </Typography>
                      <TextField
                        variant="outlined"
                        placeholder="www.linkedin.com/company/spookyblinders"
                        value="www.linkedin.com/company/spookyblinders"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            borderColor: "#C7C7C7",
                          },

                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#333333",
                            border: "1px solid",
                          },

                          "& .MuiOutlinedInput-input": {
                            padding: "14px 16px",
                            background: "#ffffff",
                            borderRadius: "8px",
                          },
                          "&.MuiInputBase-input::placeholder": {
                            color: "#333333",
                            fontSize: "16px",
                            opacity: "0.8",
                          },
                        }}
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                      <Typography
                        color="#333333"
                        fontSize="14px"
                        fontWeight="500"
                        mb="12px"
                        sx={{ opacity: "0.6" }}
                      >
                        Twitter
                      </Typography>
                      <TextField
                        variant="outlined"
                        placeholder="www.twitter.com/ryanlee"
                        value="www.twitter.com/ryanlee"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            borderColor: "#C7C7C7",
                          },

                          "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#333333",
                            border: "1px solid",
                          },

                          "& .MuiOutlinedInput-input": {
                            padding: "14px 16px",
                            background: "#ffffff",
                            borderRadius: "8px",
                          },
                          "&.MuiInputBase-input::placeholder": {
                            color: "#333333",
                            fontSize: "16px",
                            opacity: "0.8",
                          },
                        }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid> */}
            </Grid>)}
          {/* first panel content */}

          {/* second panel content */}
          {value === 1 && (
            <>
              <Grid container width="fit-content">
                <Grid
                  size={{ xs: 12, sm: 12, md: 12 }}
                  mt="28px"
                  borderRadius="20px"
                  sx={{ background: "#fff", padding: { xs: "16px !important", sm: "24px !important", md: "32px !important" } }}
                  boxShadow="0px 4px 12px 0px rgba(43, 43, 48, 0.06)"
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#333333", mb: "20px", maxWidth: "530px", lineHeight: "171%" }}
                  >
                    Choose a strong password with at least 8 characters, including a
                    mix of uppercase, lowercase, numbers, and symbols for better
                    security
                  </Typography>
                  <Box maxWidth={"400px"}>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          color="#333333"
                          fontSize="14px"
                          fontWeight="500"
                          mb="12px"
                          sx={{ opacity: "0.6" }}
                        >
                          Current Password
                        </Typography>
                        <TextField
                          autoComplete="off"
                          variant="outlined"
                          value={oldPassword}
                          onChange={handleOldPasswordChange}
                          type={showOldPassword ? "text" : "password"}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                              background: "#ffffff",
                              paddingRight: "0",
                            },

                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },

                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    onClick={handleClickShowOldPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    onMouseUp={handleMouseUpPassword}
                                    disabled={!oldPassword}
                                    sx={{
                                      opacity: oldPassword ? 1 : 0.5,
                                      cursor: oldPassword ? 'pointer' : 'default'
                                    }}
                                  >
                                    {showOldPassword ? (
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z"
                                          stroke="#333333"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M12.0001 20.27C15.5301 20.27 18.8201 18.19 21.1101 14.59C22.0101 13.18 22.0101 10.81 21.1101 9.39997C18.8201 5.79997 15.5301 3.71997 12.0001 3.71997C8.47009 3.71997 5.18009 5.79997 2.89009 9.39997C1.99009 10.81 1.99009 13.18 2.89009 14.59C5.18009 18.19 8.47009 20.27 12.0001 20.27Z"
                                          stroke="#333333"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    ) : (
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M14.5299 9.47004L9.46992 14.53C8.81992 13.88 8.41992 12.99 8.41992 12C8.41992 10.02 10.0199 8.42004 11.9999 8.42004C12.9899 8.42004 13.8799 8.82004 14.5299 9.47004Z"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M17.8201 5.76998C16.0701 4.44998 14.0701 3.72998 12.0001 3.72998C8.47009 3.72998 5.18009 5.80998 2.89009 9.40998C1.99009 10.82 1.99009 13.19 2.89009 14.6C3.68009 15.84 4.60009 16.91 5.60009 17.77"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M8.41992 19.5301C9.55992 20.0101 10.7699 20.2701 11.9999 20.2701C15.5299 20.2701 18.8199 18.1901 21.1099 14.5901C22.0099 13.1801 22.0099 10.8101 21.1099 9.40005C20.7799 8.88005 20.4199 8.39005 20.0499 7.93005"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M15.5099 12.7C15.2499 14.11 14.0999 15.26 12.6899 15.52"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M9.47 14.53L2 22"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M22 2L14.53 9.47"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box
                                    display={"flex"}
                                    onClick={oldPassword ? handleClickShowOldPassword : undefined}
                                    onMouseDown={oldPassword ? () => handleMouseDownPassword : undefined}
                                    onMouseUp={oldPassword ? () => handleMouseUpPassword : undefined}
                                    sx={{
                                      opacity: oldPassword ? 1 : 0.5,
                                      cursor: oldPassword ? 'pointer' : 'default'
                                    }}
                                  >
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </Box>
                                </InputAdornment>
                              ),
                            },
                          }}
                          fullWidth
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          color="#333333"
                          fontSize="14px"
                          fontWeight="500"
                          mb="12px"
                          sx={{ opacity: "0.6" }}
                        >
                          New Password
                        </Typography>
                        <TextField
                          autoComplete="off"
                          variant="outlined"
                          value={newPassword}
                          onChange={handleNewPasswordChange}
                          type={showNewPassword ? "text" : "password"}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: "#C7C7C7",
                              background: "#ffffff",
                              paddingRight: "0",
                            },

                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "#333333",
                              border: "1px solid",
                            },

                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    onClick={handleClickShowNewPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    onMouseUp={handleMouseUpPassword}
                                    disabled={!newPassword}
                                    sx={{
                                      opacity: newPassword ? 1 : 0.5,
                                      cursor: newPassword ? 'pointer' : 'default'
                                    }}
                                  >
                                    {showNewPassword ? (
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z"
                                          stroke="#333333"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M12.0001 20.27C15.5301 20.27 18.8201 18.19 21.1101 14.59C22.0101 13.18 22.0101 10.81 21.1101 9.39997C18.8201 5.79997 15.5301 3.71997 12.0001 3.71997C8.47009 3.71997 5.18009 5.79997 2.89009 9.39997C1.99009 10.81 1.99009 13.18 2.89009 14.59C5.18009 18.19 8.47009 20.27 12.0001 20.27Z"
                                          stroke="#333333"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    ) : (
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M14.5299 9.47004L9.46992 14.53C8.81992 13.88 8.41992 12.99 8.41992 12C8.41992 10.02 10.0199 8.42004 11.9999 8.42004C12.9899 8.42004 13.8799 8.82004 14.5299 9.47004Z"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M17.8201 5.76998C16.0701 4.44998 14.0701 3.72998 12.0001 3.72998C8.47009 3.72998 5.18009 5.80998 2.89009 9.40998C1.99009 10.82 1.99009 13.19 2.89009 14.6C3.68009 15.84 4.60009 16.91 5.60009 17.77"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M8.41992 19.5301C9.55992 20.0101 10.7699 20.2701 11.9999 20.2701C15.5299 20.2701 18.8199 18.1901 21.1099 14.5901C22.0099 13.1801 22.0099 10.8101 21.1099 9.40005C20.7799 8.88005 20.4199 8.39005 20.0499 7.93005"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M15.5099 12.7C15.2499 14.11 14.0999 15.26 12.6899 15.52"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M9.47 14.53L2 22"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M22 2L14.53 9.47"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box
                                    display={"flex"}
                                    onClick={newPassword ? handleClickShowNewPassword : undefined}
                                    onMouseDown={newPassword ? () => handleMouseDownPassword : undefined}
                                    onMouseUp={newPassword ? () => handleMouseUpPassword : undefined}
                                    sx={{
                                      opacity: newPassword ? 1 : 0.5,
                                      cursor: newPassword ? 'pointer' : 'default'
                                    }}
                                  >
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </Box>
                                </InputAdornment>
                              ),
                            },
                          }}
                          fullWidth
                        />
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          mt="12px"
                        >
                          {[...Array(4)].map((_, index) => (
                            <Box
                              key={index}
                              height="4px"
                              borderRadius="2px"
                              bgcolor={
                                index < calculatePasswordStrength(newPassword)
                                  ? getStrengthColor(
                                    calculatePasswordStrength(newPassword)
                                  )
                                  : "#D9D9D9"
                              }
                              width="calc(100%/4 - 6px)"
                            />
                          ))}
                        </Box>
                        {newPassword && (
                          <Typography
                            variant="caption"
                            color={getStrengthColor(
                              calculatePasswordStrength(newPassword)
                            )}
                          >
                            {getStrengthText(calculatePasswordStrength(newPassword))}
                          </Typography>
                        )}
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          color="#333333"
                          fontSize="14px"
                          fontWeight="500"
                          mb="12px"
                          sx={{ opacity: "0.6" }}
                        >
                          Confirm New Password
                        </Typography>
                        <TextField
                          autoComplete="off"
                          variant="outlined"
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          type={showConfirmPassword ? "text" : "password"}
                          error={!!passwordError}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              borderColor: passwordError ? "#E44258" : "#C7C7C7",
                              background: "#ffffff",
                              paddingRight: "0",
                            },
                            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: passwordError ? "#E44258" : "#333333",
                              border: "1px solid",
                            },
                            "& .MuiOutlinedInput-input": {
                              padding: "14px 16px",
                              background: "#ffffff",
                              borderRadius: "8px",
                            },
                            "&.MuiInputBase-input::placeholder": {
                              color: "#333333",
                              fontSize: "16px",
                              opacity: "0.8",
                            },
                          }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="start">
                                  <IconButton
                                    onClick={handleClickShowConfirmPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    onMouseUp={handleMouseUpPassword}
                                    disabled={!confirmPassword}
                                    sx={{
                                      opacity: confirmPassword ? 1 : 0.5,
                                      cursor: confirmPassword ? 'pointer' : 'default'
                                    }}
                                  >
                                    {showConfirmPassword ? (
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z"
                                          stroke="#333333"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M12.0001 20.27C15.5301 20.27 18.8201 18.19 21.1101 14.59C22.0101 13.18 22.0101 10.81 21.1101 9.39997C18.8201 5.79997 15.5301 3.71997 12.0001 3.71997C8.47009 3.71997 5.18009 5.79997 2.89009 9.39997C1.99009 10.81 1.99009 13.18 2.89009 14.59C5.18009 18.19 8.47009 20.27 12.0001 20.27Z"
                                          stroke="#333333"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    ) : (
                                      <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M14.5299 9.47004L9.46992 14.53C8.81992 13.88 8.41992 12.99 8.41992 12C8.41992 10.02 10.0199 8.42004 11.9999 8.42004C12.9899 8.42004 13.8799 8.82004 14.5299 9.47004Z"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M17.8201 5.76998C16.0701 4.44998 14.0701 3.72998 12.0001 3.72998C8.47009 3.72998 5.18009 5.80998 2.89009 9.40998C1.99009 10.82 1.99009 13.19 2.89009 14.6C3.68009 15.84 4.60009 16.91 5.60009 17.77"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M8.41992 19.5301C9.55992 20.0101 10.7699 20.2701 11.9999 20.2701C15.5299 20.2701 18.8199 18.1901 21.1099 14.5901C22.0099 13.1801 22.0099 10.8101 21.1099 9.40005C20.7799 8.88005 20.4199 8.39005 20.0499 7.93005"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M15.5099 12.7C15.2499 14.11 14.0999 15.26 12.6899 15.52"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M9.47 14.53L2 22"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                        <path
                                          d="M22 2L14.53 9.47"
                                          stroke="#BABABA"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Box
                                    display={"flex"}
                                    onClick={confirmPassword ? handleClickShowConfirmPassword : undefined}
                                    onMouseDown={confirmPassword ? () => handleMouseDownPassword : undefined}
                                    onMouseUp={confirmPassword ? () => handleMouseUpPassword : undefined}
                                    sx={{
                                      opacity: confirmPassword ? 1 : 0.5,
                                      cursor: confirmPassword ? 'pointer' : 'default'
                                    }}
                                  >
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                      <path
                                        d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z"
                                        stroke="#333333"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </Box>
                                </InputAdornment>
                              ),
                            },
                          }}
                          fullWidth
                        />
                        {passwordError && (
                          <Typography
                            color="#E44258"
                            fontSize="12px"
                            mt="8px"
                            display="flex"
                            alignItems="center"
                            gap="4px"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8C14.6667 4.3181 11.6819 1.33333 8 1.33333C4.3181 1.33333 1.33333 4.3181 1.33333 8C1.33333 11.6819 4.3181 14.6667 8 14.6667Z"
                                stroke="#E44258"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 5.33333V8"
                                stroke="#E44258"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 10.6667H8.00667"
                                stroke="#E44258"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {passwordError}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
              <Button
                sx={{
                  textTransform: "capitalize",
                  bgcolor: "#178EFC",
                  color: "#FFFFFF !important",
                  px: "24px",
                  height: "52px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  "&:hover": {
                    border: "none !important",
                    bgcolor: "#066fcffa",
                  },
                  marginTop: "32px",
                  paddingX: "64px",
                  "&.Mui-disabled": {
                    bgcolor: "#E8EAF0",
                    color: "#8F8F8F !important",
                  },
                }}
                disabled={
                  !!passwordError ||
                  !oldPassword ||
                  !newPassword ||
                  !confirmPassword ||
                  client_email
                }
                onClick={handleUpdatePassword}
              >
                Update
              </Button>
            </>
          )}
          {/* second panel content */}

          {/* third panel content */}
          {value === 2 && (
            <TableContainer component={Paper} sx={{ boxShadow: '0px 4px 12px 0px #2B2B300F', p: { xs: "12px", sm: "24px", lg: "32px" }, m: "28px 0", borderRadius: "20px" }} >
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Full Name (Email)</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400", minWidth: "167px" }}>Company Name</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', width: '20%', fontWeight: "400" }}>Sub-Domain</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', width: '20%', fontWeight: "400" }}>Custom Domain</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400", minWidth: "148px" }}>Status <Typography variant="body2" color="">(Custom Domain )</Typography></TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                {isLoading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ border: 'none', py: 3 }}>
                        <Loader fullHeight minHeight='200px'/>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : companies.length === 0 ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ border: 'none', py: 3 }}>
                        <Typography color="textSecondary">No domains found</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {companies.map((company) => (
                      <TableRow
                        key={company.userId}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left" component="th" scope="row" sx={{ color: '#231F1F', fontSize: '18px', borderBottom: '1px solid #E8EAF0' }}>
                          <Box display="flex" alignItems="center" gap="10px">
                            <Avatar alt={company?.name} src={company?.picture} sx={{ color: "#333333", bgcolor: "#5aaffd42", fontSize: "16px" }}>
                              {!company?.picture && company?.firstName?.[0]}
                            </Avatar>
                            <Box>
                              <Typography fontSize="16px">
                                {company.name}
                              </Typography>
                              <Typography variant='body2' sx={{
                                maxWidth: '210px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                                title={company.email}>{company.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="left" sx={{ color: '#231F1F', borderBottom: '1px solid #E8EAF0', fontSize: '14px' }}>{company.companyName}</TableCell>
                        <TableCell align="left" sx={{
                          color: '#231F1F', borderBottom: '1px solid #E8EAF0', fontSize: '14px', minWidth: "167px", maxWidth: '168px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={`${company.domains?.easywebinar}.easywebinar.live`}>{company.domains?.easywebinar}.easywebinar.live</TableCell>
                        <TableCell align="left" sx={{
                          color: '#231F1F', borderBottom: '1px solid #E8EAF0', fontSize: '14px', minWidth: "167px", maxWidth: '168px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={company.customDomain}>{company.customDomain || "--"}</TableCell>
                        <TableCell align="left" sx={{ color: '#231F1F', fontSize: '14px', borderBottom: '1px solid #E8EAF0' }}>
                          <Box display="flex">
                            <Box marginRight="20px" display="flex">
                              <Button
                                variant="outlined"
                                sx={{
                                  borderRadius: "4px",
                                  textTransform: "capitalize",
                                  border: company.customDomainStatus === "ACTIVE" ? "1px solid #22B060" : "1px solid #C42339",
                                  background: company.customDomainStatus === "ACTIVE" ? "rgba(34, 176, 96, 0.09)" : "rgba(249, 218, 222, 0.60)",
                                  color: company.customDomainStatus === "ACTIVE" ? "#22B060" : "#C42339",
                                  fontSize: "12px",
                                  height: "26px",
                                  marginLeft: "8px"
                                }}
                              >
                                {company.customDomainStatus}
                              </Button>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="left" sx={{ cursor: 'pointer' }}><Box display="flex" gap="10px" className='user-table-action'>
                          {hasCustomDomainAddOn(company) ? (
                            <>
                              <Tooltip title="Edit" arrow>
                                <Box
                                  width="28px"
                                  height="28px"
                                  borderRadius="4px"
                                  border="1px solid #E8EAF0"
                                  padding="2px"
                                  display="flex"
                                  sx={{ cursor: 'pointer', ":hover": { borderColor: "#666666" } }}
                                  onClick={() => handleCompanyAction(company, 'edit')}
                                >
                                  <img src="https://easywebinar-site.easywebinar.com/images/autheticator-app-images/edit-btn.svg" alt="edit-btn" />
                                </Box>
                              </Tooltip>
                              <Tooltip title="Cancel Custom Domain Add-on" arrow>
                                <Box
                                  width="28px"
                                  height="28px"
                                  borderRadius="4px"
                                  border="1px solid #E8EAF0"
                                  padding="2px"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{ ":hover": { borderColor: "#666666" }, "& svg": { height: "18px", width: "18px" } }}
                                  onClick={() => handleCompanyAction(company, 'cancel')}
                                >
                                  <ModalClose />
                                </Box>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Buy Add-On" arrow>
                              <Box
                                width="28px"
                                height="28px"
                                borderRadius="4px"
                                border="1px solid #E8EAF0"
                                padding="2px"
                                display="flex"
                                sx={{ cursor: 'pointer', ":hover": { borderColor: "#666666" } }}
                                onClick={() => handleCompanyAction(company, 'buy')}
                              >
                                <CartIcon />
                              </Box>
                            </Tooltip>
                          )}
                        </Box></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
            </TableContainer>)}
          {/* third panel content */}

          <UpdateUserDomainModal
            open={editCustomDomainDialogOpen}
            onClose={handleCustomDomainUpdateClose}
            domain=".easywebinar.live"
            onConfirm={handleCustomDomainUpdate}
            subdomain={selectedCompany?.domains?.easywebinar || ''}
            customDomain={selectedCompany?.customDomain || ''}
            customDomainStatus={selectedCompany?.customDomainStatus || ''}
            companyId={selectedCompany?.companyId || ''}
            cognitoUserId={selectedCompany?.userId || ''}
          />

          <DeletePopup
            open={deleteAddOnDialog}
            onClose={handleCancelClose}
            onConfirm={handleCancelAddOnConfirm}
            title="Unsubscribe Add-On"
            content="Are you sure you want to unsubscribe Custom Domain from this User?"
          />

          <BuyAddOns
            open={buyAddOnsDialog}
            onClose={handleDialogBuyAddOnsClose}
            onConfirm={handleDialogBuyAddOnsConfirm}
            subscriptionId={selectedCompany?.subscription?.id}
            subscribedAddons={selectedCompany?.subscription?.items?.filter(item => item.itemType === 'addon').map(addon => addon.itemPriceId) || []}
          />

        </Box>

        {/* Add hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handlePhotoUpload}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </Box>
    </>
  );
};

export default Settings;
